/*
 * layout
 */
import { useAuth, ProfileDropdown } from "@emr/auth/client"
import { appConfig } from "@emr/config"
import { useDevMode } from "@emr/dev"
import { LanguageSelect } from "@emr/intl"
import { ErrorBoundary, Footer, Navbar } from "@emr/react-components"
import { ThemeToggle } from "@emr/theme"
import { getEnvDomainName, AppTitle } from "@emr/utils"
import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Sidebar } from "./components/sidebar/sidebar"
import { SidebarOptions } from "./components/sidebar/sidebar-options"
import { SidebarSheetMenu } from "./components/sidebar/sidebar-sheet-menu"
import { getSidebarMenuList } from "./config/sidebar"
import { trpc } from "./trpc"

function BrandLogo({ size = 7 }: { size?: number }) {
	return (
		<img
			src="/assets/logo/transparent_logo.png"
			alt="Valor Wellness"
			className={`h-${size} w-auto object-contain opacity-85 dark:opacity-75`}
		/>
	)
}

type LayoutProps = {
	children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
	const { user, role } = useAuth()
	const { devMode } = useDevMode()
	const { t } = useTranslation()

	const isStaff =
		role === "practitioner" ||
		role === "nurse" ||
		role === "sla_specialist" ||
		role === "admin" ||
		role === "super_admin"

	const { data: pendingPatients } = trpc.user.getPendingPatients.useQuery(undefined, {
		enabled: isStaff,
		staleTime: 60_000,
	})
	const { data: statusCounts } = trpc.patient.getStatusCounts.useQuery(undefined, {
		enabled: isStaff,
		staleTime: 30_000,
	})
	const { data: unreadMessages } = trpc.telemedicineChat.getUnreadCount.useQuery(undefined, {
		enabled: isStaff,
		staleTime: 30_000,
		refetchInterval: 60_000,
	})
	const sidebarFlags = {
		pendingPatientCount: pendingPatients?.length,
		statusCounts,
		messagingUnreadCount: unreadMessages?.count,
	}

	const enabledSidebar = !!user

	return (
		<div className={"flex size-full flex-row"}>
			{enabledSidebar && (
				<>
					<Sidebar
						title={appConfig.brand.title}
						TitleIcon={<BrandLogo size={7} />}
						// initialSidebarIsOpen={initialSidebarIsOpen}
						// bottomContent={
						// 	<PowerLogoutButton
						// 		className={"pt-2"}
						// 		initialSidebarIsOpen={initialSidebarIsOpen}
						// 	>
						// 		{t("SignOut")}
						// 	</PowerLogoutButton>
						// }
						key={user?.id}
					>
						<SidebarOptions
							user={user}
							devMode={devMode}
							flags={sidebarFlags}
							getSidebarMenuList={getSidebarMenuList}
						/>
					</Sidebar>
				</>
			)}
			<div className={"flex size-full flex-col"}>
				<Navbar
					className={"transition-[width] duration-300 ease-in-out"}
					title={AppTitle}
					titleHref={"/"}
					Sidebar={
						enabledSidebar ? (
							<SidebarSheetMenu
								title={appConfig.brand.title}
								TitleIcon={<BrandLogo size={7} />}
								// bottomContent={
								// 	<PowerLogoutButton className={"pt-2"}>
								// 		{t("SignOut")}
								// 	</PowerLogoutButton>
								// }
								key={user?.id}
							>
								<SidebarOptions
									user={user}
									devMode={devMode}
									flags={sidebarFlags}
									getSidebarMenuList={getSidebarMenuList}
								/>
							</SidebarSheetMenu>
						) : undefined
					}
					RightSide={
						<div className="flex items-center gap-2">
							<ThemeToggle />
							<ProfileDropdown
								user={user || undefined}
								registerUrl={"/auth?tab=register"}
								loginUrl={"/auth?tab=login"}
								logoutUrl={"/auth/logout"}
								accountUrl={"/auth/account"}
							/>
						</div>
					}
				/>
				<main
					className={
						"relative flex-1 transition-[width] overflow-y-hidden duration-300 ease-in-out"
					}
				>
					<ErrorBoundary>{children}</ErrorBoundary>
				</main>
				<Footer
					copyright={"© 2026 Timeprox"}
					copyrightHref={getEnvDomainName("https://timeprox.com")}
					RightSide={<LanguageSelect />}
					devMode={devMode}
					whitelistPathnames={["/", "/about", "/auth", "/waitlist"]}
				>
					<Link className={"text-inherit"} to={"/"}>
						{t("nav.home")}
					</Link>
					<Link className={"text-inherit"} to={"/about"}>
						{t("nav.about")}
					</Link>
				</Footer>
			</div>
		</div>
	)
}
